import { L, type Localized, type MessageKey } from "./i18n";
export const screens = [
  "work-queue",
  "dashboard",
  "clients",
  "communication",
  "bank",
  "mailroom",
  "invoicing",
  "court",
  "forms",
  "portal",
  "reports",
  "knowledge",
  "settings",
  "budget",
  "debts",
] as const;
export type Screen = (typeof screens)[number] | "client";
export const navigation: {
  screen: Screen;
  label: MessageKey;
  icon: string;
  group?: MessageKey;
  badge?: number;
}[] = [
  { screen: "work-queue", label: "workQueue", icon: "queue" },
  { screen: "dashboard", label: "dashboard", icon: "dashboard" },
  { screen: "clients", label: "clients", icon: "users", group: "clients" },
  { screen: "communication", label: "communication", icon: "chat", badge: 2 },
  { screen: "bank", label: "bank", icon: "bank", group: "moneyMail", badge: 6 },
  { screen: "mailroom", label: "mailroom", icon: "mail", badge: 12 },
  { screen: "invoicing", label: "invoicing", icon: "file" },
  { screen: "court", label: "court", icon: "court", group: "external" },
  { screen: "forms", label: "forms", icon: "form" },
  { screen: "portal", label: "portal", icon: "phone" },
  { screen: "reports", label: "reports", icon: "chart", group: "office" },
  { screen: "knowledge", label: "knowledge", icon: "book" },
  { screen: "settings", label: "settings", icon: "settings" },
];
export type Task = {
  id: string;
  category: "post" | "bank" | "portal" | "communication" | "court";
  priority: "high" | "medium" | "low";
  client: string;
  time: string;
  title: Localized;
  description: Localized;
  proposal: Localized;
  source: Localized;
  ai: boolean;
};
export const initialTasks: Task[] = [
  {
    id: "tax",
    category: "post",
    priority: "high",
    client: "J. Voorbeeld · 0143",
    time: "09:12",
    ai: true,
    title: L(
      "Naheffing Belastingdienst van € 312 herkend",
      "Tax assessment of €312 identified",
    ),
    description: L(
      "Brief gelezen: aanslagnummer 8467.23.114. Geen bestaande schuld gevonden. Het budget laat een eenmalige betaling niet toe; vrije buffer € 118.",
      "Letter reviewed: assessment 8467.23.114. No existing debt found. The budget cannot accommodate a single payment; available buffer €118.",
    ),
    proposal: L(
      "Registreer als schuld, vraag een betalingsregeling aan en plan over 14 dagen een controle op kwijtschelding.",
      "Register the debt, request a payment arrangement and schedule a remission review in 14 days.",
    ),
    source: L(
      "Belastingdienst · naheffingsbrief 02-07-2026; budgetplan dossier 0143; beleidsregel S-03.",
      "Tax authority · assessment letter 02 July 2026; budget plan for case 0143; policy S-03.",
    ),
  },
  {
    id: "rent-chat",
    category: "communication",
    priority: "high",
    client: "D. Prins · 0112",
    time: "08:55",
    ai: false,
    title: L(
      "Bezorgd bericht over de huurbetaling",
      "Concerned message about the rent payment",
    ),
    description: L(
      "“Dit is nu al de derde keer dat mijn huur te laat is!” De huur is op de 3e betaald. De incasso bij de verhuurder is mislukt. Een persoonlijke reactie is nodig.",
      "“This is the third time my rent has been late!” Rent was paid on the 3rd. The landlord’s direct debit failed. A personal response is needed.",
    ),
    proposal: L(
      "Bekijk de feiten en reageer zelf. Er staat een concept met bronverwijzingen klaar.",
      "Review the facts and respond personally. A draft with source references is ready.",
    ),
    source: L(
      "Portaalbericht D. Prins 03-07-2026; banktransactie #22190.",
      "Portal message from D. Prins, 03 July 2026; bank transaction #22190.",
    ),
  },
  {
    id: "rent",
    category: "bank",
    priority: "medium",
    client: "M. de Tester · 0078",
    time: "08:40",
    ai: true,
    title: L(
      "Huur niet afgeschreven · verwacht bedrag € 745",
      "Rent not debited · expected amount €745",
    ),
    description: L(
      "Drie maanden op rij correct betaald, nu twee dagen te laat. Het saldo is toereikend. Mogelijk is de incasso bij de verhuurder mislukt.",
      "Paid correctly for three consecutive months, now two days late. There are sufficient funds. The landlord’s direct debit may have failed.",
    ),
    proposal: L(
      "Zet een handmatige betaling klaar voor de tweede controle en informeer de verhuurder.",
      "Prepare a manual payment for second review and inform the landlord.",
    ),
    source: L(
      "Banktransacties april–juli 2026; budgetregel huur; saldo rekening 0078.",
      "Bank transactions April–July 2026; rent budget rule; account balance 0078.",
    ),
  },
  {
    id: "allowance",
    category: "portal",
    priority: "medium",
    client: "K. van Proef · 0031",
    time: "08:21",
    ai: true,
    title: L(
      "Verzoek extra leefgeld € 75 · boven het kader",
      "Request for €75 extra spending money · above policy limit",
    ),
    description: L(
      "De automatische grens is € 50 per maand bij voldoende saldo. Dit verzoek wacht daarom op jouw besluit.",
      "The automatic limit is €50 per month with sufficient funds. This request therefore requires your decision.",
    ),
    proposal: L(
      "Controleer de reden en het saldo. Keur eenmalig goed of bespreek een alternatief met de cliënt.",
      "Review the reason and balance. Approve once or discuss an alternative with the client.",
    ),
    source: L(
      "Cliëntverzoek 03-07-2026; AI-beleidsregel L-01; actueel budget.",
      "Client request 03 July 2026; AI policy L-01; current budget.",
    ),
  },
  {
    id: "court",
    category: "court",
    priority: "high",
    client: "R. Placeholder · 0166",
    time: "08:05",
    ai: false,
    title: L(
      "Boedelbeschrijving · nog vier dagen",
      "Estate inventory · four days remaining",
    ),
    description: L(
      "Het concept is voor 80% gevuld. De waarde van de inboedel en de handtekening ontbreken nog.",
      "The draft is 80% complete. The household contents valuation and signature are still missing.",
    ),
    proposal: L(
      "Vul de ontbrekende gegevens aan en controleer het concept voor indiening.",
      "Complete the missing information and review the draft before submission.",
    ),
    source: L(
      "Rechtbank Den Haag; benoemingsbeschikking dossier 0166; concept-boedelbeschrijving.",
      "The Hague court; appointment order for case 0166; draft estate inventory.",
    ),
  },
  {
    id: "energy",
    category: "post",
    priority: "low",
    client: "J. Voorbeeld · 0143",
    time: "07:50",
    ai: true,
    title: L(
      "Nieuw energiecontract · budgetwijziging voorgesteld",
      "New energy contract · budget update proposed",
    ),
    description: L(
      "Het huidige contract loopt op 31 augustus af. Het nieuwe maandbedrag is € 134, een stijging van € 40.",
      "The current contract ends on 31 August. The new monthly amount is €134, an increase of €40.",
    ),
    proposal: L(
      "Pas de budgetregel aan per 1 september en verhoog de reservering. Het leefgeld blijft € 90.",
      "Update the budget rule from 1 September and increase the reserve. Spending money stays at €90.",
    ),
    source: L(
      "Energiecontract ontvangen via WhatsApp; budgetplan dossier 0143.",
      "Energy contract received via WhatsApp; budget plan for case 0143.",
    ),
  },
];
export type Client = {
  id: string;
  name: string;
  fullName: string;
  type: "guardianship" | "debtSupport" | "budgetManagement" | "mentoring";
  phase: "stable" | "intake" | "arrangement" | "stabilization";
  handler: string;
  signals: number;
};
export const initialClients: Client[] = [
  {
    id: "0143",
    name: "J. Voorbeeld",
    fullName: "Jamal Voorbeeld",
    type: "debtSupport",
    phase: "arrangement",
    handler: "Jasper K.",
    signals: 2,
  },
  {
    id: "0078",
    name: "M. de Tester",
    fullName: "M. de Tester",
    type: "guardianship",
    phase: "stable",
    handler: "Jasper K.",
    signals: 1,
  },
  {
    id: "0102",
    name: "A. Demo",
    fullName: "A. Demo",
    type: "guardianship",
    phase: "stable",
    handler: "Sanne V.",
    signals: 1,
  },
  {
    id: "0055",
    name: "B. Fictief",
    fullName: "B. Fictief",
    type: "budgetManagement",
    phase: "intake",
    handler: "Sanne V.",
    signals: 0,
  },
  {
    id: "0031",
    name: "K. van Proef",
    fullName: "K. van Proef",
    type: "guardianship",
    phase: "stable",
    handler: "Jasper K.",
    signals: 1,
  },
  {
    id: "0112",
    name: "D. Prins",
    fullName: "D. Prins",
    type: "guardianship",
    phase: "stable",
    handler: "Jasper K.",
    signals: 1,
  },
  {
    id: "0166",
    name: "R. Placeholder",
    fullName: "R. Placeholder",
    type: "mentoring",
    phase: "intake",
    handler: "Piet D.",
    signals: 1,
  },
  {
    id: "0121",
    name: "P. Janssen",
    fullName: "P. Janssen",
    type: "debtSupport",
    phase: "stabilization",
    handler: "Piet D.",
    signals: 0,
  },
  {
    id: "0089",
    name: "T. Willems",
    fullName: "T. Willems",
    type: "guardianship",
    phase: "stable",
    handler: "Sanne V.",
    signals: 0,
  },
  {
    id: "0171",
    name: "S. Bakker",
    fullName: "S. Bakker",
    type: "guardianship",
    phase: "intake",
    handler: "Piet D.",
    signals: 0,
  },
];
export const payments = [
  {
    id: "p1",
    case: "0078",
    to: "Woongoed B.V.",
    amount: 74500,
    origin: L("Budgetregel huur", "Rent budget rule"),
    safe: true,
  },
  {
    id: "p2",
    case: "0102",
    to: "CZ Zorgverzekering",
    amount: 13000,
    origin: L("Budgetregel zorg", "Health insurance rule"),
    safe: true,
  },
  {
    id: "p3",
    case: "0121",
    to: "Evides Waterbedrijf",
    amount: 3150,
    origin: L("Budgetregel water", "Water budget rule"),
    safe: true,
  },
  {
    id: "p4",
    case: "0143",
    to: "J. Voorbeeld",
    amount: 9000,
    origin: L("Leefgeldregeling", "Spending money allowance"),
    safe: true,
  },
  {
    id: "p5",
    case: "0143",
    to: "NL91 ••",
    amount: 25000,
    origin: L("Nieuwe tegenrekening", "New counterparty"),
    safe: false,
  },
  {
    id: "p6",
    case: "0055",
    to: "Ziggo B.V.",
    amount: 5250,
    origin: L("Mogelijke dubbele incasso", "Possible duplicate debit"),
    safe: false,
  },
];
export const budgetRows = [
  {
    id: "rent",
    label: L("Huur", "Rent"),
    frequency: "monthly" as const,
    budget: 74500,
    actual: 74500,
  },
  {
    id: "energy",
    label: L("Energie", "Energy"),
    frequency: "monthly" as const,
    budget: 9400,
    actual: 9400,
  },
  {
    id: "spending",
    label: L("Leefgeld", "Spending money"),
    frequency: "weekly" as const,
    budget: 9000,
    actual: 9000,
  },
  {
    id: "health",
    label: L("Zorgverzekering", "Health insurance"),
    frequency: "monthly" as const,
    budget: 13000,
    actual: 13000,
  },
  {
    id: "repayment",
    label: L("Aflossing regeling", "Debt repayment"),
    frequency: "monthly" as const,
    budget: 19200,
    actual: 19200,
  },
  {
    id: "streaming",
    label: L("Streamingdienst", "Streaming service"),
    frequency: "monthly" as const,
    budget: 0,
    actual: 1299,
  },
];
export const mailItems = [
  {
    id: "m1",
    title: L("Belastingdienst · naheffing", "Tax authority · assessment"),
    case: "0143",
    match: 100,
    proposal: L("Schuld + regeling", "Debt + arrangement"),
    amount: 31200,
  },
  {
    id: "m2",
    title: L("Beschikking bijzondere bijstand", "Special assistance decision"),
    case: "0102",
    match: 98,
    proposal: L("Inkomstenregel aanpassen", "Update income rule"),
    amount: 13310,
  },
  {
    id: "m3",
    title: L("CJIB · aanmaning", "CJIB · payment reminder"),
    case: "0055",
    match: 91,
    proposal: L("Schuld bijwerken", "Update debt"),
    amount: 8400,
  },
  {
    id: "m4",
    title: L("UWV · beslissing WIA", "UWV · disability benefit decision"),
    case: "0089",
    match: 95,
    proposal: L("Inkomen aanpassen", "Update income"),
    amount: 158600,
  },
  {
    id: "m5",
    title: L("Woongoed · huurverhoging", "Woongoed · rent increase"),
    case: "0078",
    match: 94,
    proposal: L("Budgetregel aanpassen", "Update budget rule"),
    amount: 74500,
  },
  {
    id: "m6",
    title: L("Onleesbare scan (pagina 3)", "Unreadable scan (page 3)"),
    case: "—",
    match: 40,
    proposal: L("Handmatig toewijzen", "Assign manually"),
    amount: 0,
  },
];
export const debtRows = [
  {
    name: "Belastingdienst",
    collector: "—",
    amount: 270200,
    status: L("Bevestigd · in regeling", "Confirmed · in arrangement"),
    tone: "success",
  },
  {
    name: "CZ Zorgverzekering",
    collector: "GGN",
    amount: 41500,
    status: L("Regeling · 5/9 termijnen", "Arrangement · 5/9 instalments"),
    tone: "teal",
  },
  {
    name: "LBIO",
    collector: "—",
    amount: 184000,
    status: L("Rappel klaar", "Reminder ready"),
    tone: "warning",
  },
  {
    name: "Wehkamp",
    collector: "Intrum",
    amount: 62300,
    status: L("Wacht op reactie", "Awaiting response"),
    tone: "muted",
  },
  {
    name: "DUO",
    collector: "—",
    amount: 312000,
    status: L("Bevestigd · in regeling", "Confirmed · in arrangement"),
    tone: "success",
  },
  {
    name: L("Gemeentebelastingen", "Municipal taxes"),
    collector: "—",
    amount: 38600,
    status: L("Kwijtschelding kansrijk", "Remission likely"),
    tone: "teal",
  },
  {
    name: "Evides",
    collector: "—",
    amount: 0,
    status: L("Afgelost", "Paid off"),
    tone: "success",
  },
];
export const formItems = [
  {
    title: L("ING-beheerformulier", "ING account management form"),
    description: L(
      "Rekeningen openen of wijzigen. Cliëntgegevens, beschikking en kantoorprofiel worden vooraf ingevuld.",
      "Open or change accounts. Client details, appointment order and office profile are prefilled.",
    ),
    version: "2026-03-01",
    icon: "bank",
  },
  {
    title: L("Bijzondere bijstand", "Special assistance"),
    description: L(
      "Gemeente Middelburg. Met een checklist voor de beschikking, inkomensspecificatie en offerte.",
      "Municipality of Middelburg. Includes a checklist for the appointment order, income statement and quote.",
    ),
    version: "2026-01-01",
    icon: "form",
  },
  {
    title: L("Kwijtschelding gemeentebelastingen", "Municipal tax remission"),
    description: L(
      "Toets vermogen en inkomen en maak een aanvraag voor het geselecteerde dossier.",
      "Review assets and income and prepare an application for the selected case file.",
    ),
    version: "2026-04-01",
    icon: "file",
  },
  {
    title: L(
      "Beslagvrije voet · herberekening",
      "Protected income · recalculation",
    ),
    description: L(
      "Aanvraag met actuele inkomens- en woonlastgegevens uit het dossier.",
      "Application with current income and housing expenses from the case file.",
    ),
    version: "2026-05-01",
    icon: "court",
  },
  {
    title: L("DigiD-machtiging aanvragen", "Request DigiD authorisation"),
    description: L(
      "Machtigingsformulier met een duidelijke uitleg in gewone taal voor de cliënt.",
      "Authorisation form with a clear, plain-language explanation for the client.",
    ),
    version: "2025-11-01",
    icon: "shield",
  },
  {
    title: L("Aanvraag voedselbank Zeeland", "Zeeland food bank application"),
    description: L(
      "Bekijk de voorwaarden en voeg de relevante gegevens uit het budgetplan toe.",
      "Review eligibility and add the relevant information from the budget plan.",
    ),
    version: "2026-02-01",
    icon: "users",
  },
];
export const articles = [
  {
    id: "a1",
    title: L("Rappeltermijnen schuldeisers", "Creditor reminder intervals"),
    owner: "Sanne V.",
    date: "2026-06-12",
    flagged: false,
    content: L(
      "Controleer na 14 dagen of de schuldeiser heeft gereageerd. Leg het contact vast in het dossier. Controleer het concept-rappel voordat je dit verstuurt. Bij een lopende bezwaarprocedure overleg je eerst met de behandelaar.",
      "After 14 days, check whether the creditor has responded. Record contact in the case file. Review the draft reminder before sending. If an objection is in progress, consult the case handler first.",
    ),
  },
  {
    id: "a2",
    title: L(
      "Intakechecklist bewind + SHV",
      "Guardianship + debt support intake checklist",
    ),
    owner: "Jasper K.",
    date: "2026-06-03",
    flagged: false,
    content: L(
      "Verzamel de beschikking, een geldig identiteitsdocument, rekeningoverzichten en gegevens van schuldeisers. Bespreek doelen en contactvoorkeuren met de cliënt. Leg alle ontbrekende stukken vast als taak.",
      "Collect the appointment order, valid identification, account statements and creditor details. Discuss goals and contact preferences with the client. Create a task for each missing document.",
    ),
  },
  {
    id: "a3",
    title: L(
      "Omgaan met bezorgde of boze berichten",
      "Handling concerned or angry messages",
    ),
    owner: "Piet D.",
    date: "2026-05-22",
    flagged: false,
    content: L(
      "Laat een medewerker persoonlijk reageren. Erken de zorgen van de cliënt, controleer de feiten en leg in gewone taal uit wat de volgende stap is. Laat de AI het bericht niet zelfstandig versturen.",
      "Have a staff member respond personally. Acknowledge the client’s concerns, verify the facts and explain the next step in plain language. Do not let AI send the message independently.",
    ),
  },
  {
    id: "a4",
    title: L("Vermogensgrens toeslagen 2026", "Benefit asset limits 2026"),
    owner: "Sanne V.",
    date: "2026-01-10",
    flagged: true,
    content: L(
      "Dit artikel staat ter controle bij de kennisbeheerder. Gebruik de actuele officiële voorwaarden bij het beoordelen van een aanvraag.",
      "This article is under review by the knowledge owner. Consult current official criteria when assessing an application.",
    ),
  },
  {
    id: "a5",
    title: L(
      "Beslagvrije voet: wanneer herberekenen",
      "Protected income: when to recalculate",
    ),
    owner: "Sanne V.",
    date: "2025-11-01",
    flagged: true,
    content: L(
      "Controleer of wijzigingen in inkomen, woonsituatie of gezin aanleiding geven tot een herberekening. Dit artikel moet worden bijgewerkt; overleg met de kennisbeheerder.",
      "Check whether changes in income, housing or household composition require recalculation. This article needs updating; consult the knowledge owner.",
    ),
  },
];
